/**
 * `useMyLeaves` — TanStack Query hook for `GET /api/account/me/leaves`
 * (#336, §9.10b; #1667 §8 lifecycle fields).
 *
 * Returns the caller's leave windows across every team they actively
 * belong to. Consumed by `<LongRangeRosterSection/>` to drive the
 * §9.10b "Leave applied" calendar-overlay badge: cycle rows whose
 * date range overlaps any leave window get a calm neutral badge.
 *
 * Cache-key shape: `["me-leaves", accountId]`. Mutations from
 * `useSubmitMyLeave` invalidate this key alongside `me-schedule*` so
 * a fresh leave submission updates both the schedule view and the
 * overlay-driving leaves list. (The hook itself doesn't invalidate
 * — the mutation does — but we declare a stable queryKey here so
 * the invalidation target is consistent.)
 *
 * Types are hand-typed mirrors rather than `schema.gen.ts` reads, per
 * the `useMeScheduleWindow` convention: the generated schema is the
 * wire truth, the mirror declares only what the caller reads — and the
 * #1667 lifecycle fields have no generated entry until the backend
 * deploy lands. Every lifecycle field is OPTIONAL by contract: an OLD
 * backend payload (no `status`, no `urgent`) must still parse, render
 * and never crash — the SPA deploys ahead of the backend.
 *
 * Privacy: the endpoint is filtered server-side to the caller's
 * active memberships; the SPA does not re-filter. When the response
 * is undefined (loading / no-auth) the consumer falls back to "no
 * leaves" — safer than over-rendering the badge.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";

/** §8 lifecycle states (#1667). Absent on an OLD backend payload. */
export type MyLeaveStatus =
  | "indicated"
  | "under_review"
  | "planned"
  | "confirmed"
  | "declined"
  | "withdrawn"
  | "superseded";

export interface MyLeaveSummary {
  id: string;
  team_id: string;
  membership_id: string;
  start_date: string;
  end_date: string;
  /** `leave` | `no_oncall` | `mc` — plus, since #1667, the #836
   *  behaviours (`no_am` | `no_pm` | `prefer_am` | `prefer_pm` |
   *  `fixture`). Kept as `string` so a future behaviour never breaks
   *  an older SPA build. */
  request_type: string;
  // ── #1667 §8 lifecycle fields — ALL optional (old-backend tolerance) ──
  status?: MyLeaveStatus | string;
  urgent?: boolean;
  category_key?: string | null;
  submitted_at?: string | null;
  decline_reason?: string | null;
  decided_at?: string | null;
  /**
   * #1909 — who FILED it: `member` | `lead` | `import` | `migrated` |
   * `simulated`. A lead-reported MC used to render identically to one the
   * member filed themselves. Optional by contract: absent on an old backend.
   */
  source?: string;
  /**
   * #1909 (flows finding 23) — the recall this row repays, when it is the day
   * the call-back ladder gave back as rest. Written as an ordinary lead
   * `leave` row, so without it the calendar called the day "On leave" while
   * the Inbox said "given back as rest". Optional: absent on an old backend.
   */
  rest_after_recall_date?: string | null;
}

export interface MyLeavesListResponse {
  leaves: MyLeaveSummary[];
}

const STALE_TIME_MS = 60_000;

export function meLeavesKey(accountId: string | null) {
  return ["me-leaves", accountId ?? ""] as const;
}

/** #1667 — the Requests-ledger read (`?include=all`, settled rows too). */
export function meLeavesLedgerKey(accountId: string | null) {
  return ["me-leaves", "ledger", accountId ?? ""] as const;
}

export function useMyLeaves() {
  const { accountId } = useAuth();
  return useQuery<MyLeavesListResponse, ApiError | Error>({
    queryKey: meLeavesKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<MyLeavesListResponse>("/api/account/me/leaves"),
  });
}

/**
 * `useMyRequestsLedger` — the Tasks tab's Requests ledger (#1667 §5.5).
 *
 * `?include=all` returns EVERY lifecycle state (declined, withdrawn,
 * superseded rows included) rather than just the roster-binding slice.
 * An OLD backend ignores the query param and returns the active slice
 * without `status` — the ledger renders those rows too (no chip), so
 * the tab degrades rather than crashes during the deploy window.
 * `retry: false` + the surfaced error mirror the house degrade idiom.
 */
export function useMyRequestsLedger() {
  const { accountId } = useAuth();
  return useQuery<MyLeavesListResponse, ApiError | Error>({
    queryKey: meLeavesLedgerKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<MyLeavesListResponse>("/api/account/me/leaves?include=all"),
  });
}

/**
 * `useDeleteMyLeave` — retract a date-request the caller owns
 * (`DELETE /api/account/me/leaves/{id}`, #232 + #686).
 *
 * Covers both `request_type` values (leave + call-block) — a member
 * removes a row the same way regardless of type. Used by the member
 * own-row propose view (#687 PR-7): when a proposed fixed shift would
 * fall on a date the member has a leave / call-block on, the member can
 * choose to remove that conflicting request from the same surface.
 * Invalidates the leaves list + schedule queries on success so every
 * surface re-reads the member's current windows.
 */
export function useDeleteMyLeave() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (leaveId) =>
      apiFetch<void>(`/api/account/me/leaves/${leaveId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me-leaves"] });
      queryClient.invalidateQueries({ queryKey: ["me-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["me-schedule-horizon"] });
    },
  });
}
